import { TraversalBuilderState } from '../types';
import {
  SetTripleSortOrderPayload,
  ToggleIsSelectedGraphEdgePayload,
  ToggleIsSelectedGraphNodePayload,
} from './editTraversalActions';
import { getComponentTypeLabel } from './getPaths';
import { setTriplesOptions } from './setTraversal';

type ToggleIsSelectedGraphNodeOrEdgePayload =
  | {
      graphNodeId: string;
      type: 'graphNode';
      isForceSelected?: boolean;
    }
  | {
      graphEdgeId: string;
      type: 'graphEdge';
      isForceSelected?: boolean;
    };

export const toggleIsSelectedGraphNodeOrEdge = (
  state: TraversalBuilderState,
  payload: ToggleIsSelectedGraphNodeOrEdgePayload
): TraversalBuilderState => {
  const graphNodeId = payload.type === 'graphNode' ? payload.graphNodeId : null;
  const graphEdgeId = payload.type === 'graphEdge' ? payload.graphEdgeId : null;

  const isNewNodeSelected = Boolean(
    graphNodeId &&
    (payload.isForceSelected || graphNodeId !== state.selectedGraphNodeId)
  );

  const shouldIncludeClickOtherComponentTypeNodesHintsRestoredFromUserSettings =
    isNewNodeSelected && state.startNode?.id !== graphNodeId
      ? false
      : state.shouldIncludeClickOtherComponentTypeNodesHintsRestoredFromUserSettings;

  const newGraphNodes = new Map(state.graphNodes);
  Array.from(newGraphNodes.values()).forEach(graphNode => {
    graphNode.setIsSelected(false);
    // remove pulsing from nodes if non-context component type node has been selected
    graphNode.setMetaData({
      ...graphNode.metaData,
      shouldDisplayClickOtherComponentsHint:
        shouldIncludeClickOtherComponentTypeNodesHintsRestoredFromUserSettings,
    });
  });

  if (isNewNodeSelected && graphNodeId) {
    newGraphNodes.get(graphNodeId)!.setIsSelected(true);
  }

  const newGraphEdges = new Map(state.graphEdges);
  Array.from(newGraphEdges.values()).forEach(graphEdge => {
    graphEdge.setIsSelected(false);
  });

  const isNewEdgeSelected = Boolean(
    graphEdgeId && graphEdgeId !== state.selectedGraphEdgeId
  );

  if (isNewEdgeSelected && graphEdgeId) {
    newGraphEdges.get(graphEdgeId)?.setIsSelected(true);
  }

  return {
    ...state,
    graphNodes: newGraphNodes,
    graphEdges: newGraphEdges,
    selectedGraphNodeId: isNewNodeSelected ? graphNodeId : null,
    selectedGraphEdgeId: isNewEdgeSelected ? graphEdgeId : null,
    shouldIncludeClickOtherComponentTypeNodesHintsRestoredFromUserSettings,
    triplesSortAndFiltersState: {
      ...state.triplesSortAndFiltersState,
      filterTerm: '',
    },
  };
};

export const toggleIsSelectedGraphNodeAndUpdateTripleOptions = (
  state: TraversalBuilderState,
  { graphNodeId, isForceSelected = false }: ToggleIsSelectedGraphNodePayload
) => {
  return setTriplesOptions(
    toggleIsSelectedGraphNodeOrEdge(state, {
      graphNodeId,
      type: 'graphNode',
      isForceSelected,
    })
  );
};

export const toggleIsSelectedGraphEdgeAndUpdateTripleOptions = (
  state: TraversalBuilderState,
  { graphEdgeId }: ToggleIsSelectedGraphEdgePayload
): TraversalBuilderState => {
  return setTriplesOptions(
    toggleIsSelectedGraphNodeOrEdge(state, { graphEdgeId, type: 'graphEdge' })
  );
};

export const setTripleSortOrder = (
  state: TraversalBuilderState,
  { tripleSortOrder }: SetTripleSortOrderPayload
) => {
  const { triplesSortAndFiltersState, tripleOptions } = state;
  const newState: TraversalBuilderState = {
    ...state,
    triplesSortAndFiltersState: {
      ...triplesSortAndFiltersState,
      tripleSortOrder,
    },
  };
  return setTriplesOptions(newState, tripleOptions.instanceCountsResponse);
};

export const getSelectedNodeComponentType = (state: TraversalBuilderState) => {
  if (!state.selectedGraphNodeId) {
    return null;
  }
  return getComponentTypeLabel(state.selectedGraphNodeId, state.graphNodes);
};
